import Links from "./components/Links";
import routes from "routes";
import logo from "assets/img/sahl_black2.png";
import "./style.scss";
import { MdMenuOpen, MdLogout } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "components/ui/tooltip";
import { useDispatch } from "react-redux";
import { authActions } from "views/auth/sagas/authSlice";
import { LuUser, LuUsers, LuChevronUp } from "react-icons/lu";
import { useEffect, useRef, useState } from "react";

const ProfileButton = ({ open }: { open: boolean }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showPopup, setShowPopup] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const profileString = sessionStorage.getItem("meProfile");
  const profile = profileString ? JSON.parse(profileString) : null;
  const isSuperAdmin = profile?.isSuperAdmin === true;
  const displayName = profile?.user?.userName || profile?.userName || "Admin";
  const displayRole = isSuperAdmin ? "Super Admin" : profile?.role?.roleName || profile?.roleName || "Admin";

  // Close popup on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setShowPopup(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSignOut = () => {
    dispatch(
      authActions.logout({
        phone: sessionStorage.getItem("phone"),
        deviceToken: sessionStorage.getItem("token"),
      } as any)
    );
    sessionStorage.removeItem("token");
    navigate("/");
  };

  const handleAdminUserManagement = () => {
    setShowPopup(false);
    navigate("/superuser/admin-user-management");
  };

  // Collapsed (icon-only) mode
  if (!open) {
    return (
      <div className="relative" ref={ref}>
        <Tooltip content="Profile" contentProps={{ bg: "gray.800", color: "white" }} showArrow>
          <button
            onClick={() => setShowPopup((p) => !p)}
            className="group flex w-full items-center justify-center rounded-xl p-2.5 transition-all duration-200 hover:bg-[#2ead81]/10"
          >
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-[#2ead81] to-[#1a9a6e]">
              <LuUser className="h-3.5 w-3.5 text-white" />
            </div>
          </button>
        </Tooltip>

        {showPopup && (
          <div className="absolute bottom-12 left-0 z-[999] w-52 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-xl">
            <div className="border-b border-gray-100 px-4 py-3">
              <p className="text-xs font-bold text-navy-700">{displayName}</p>
              <p className="text-[10px] text-gray-400">{displayRole}</p>
            </div>
            <div className="p-1.5 space-y-0.5">
              {isSuperAdmin && (
                <button
                  onClick={handleAdminUserManagement}
                  className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium text-gray-700 transition-all hover:bg-[#2ead81]/10 hover:text-[#2ead81]"
                >
                  <LuUsers className="h-4 w-4" />
                  Admin User Management
                </button>
              )}
              <button
                onClick={handleSignOut}
                className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium text-gray-700 transition-all hover:bg-red-50 hover:text-red-600"
              >
                <MdLogout className="h-4 w-4" />
                Sign Out
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Expanded mode
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setShowPopup((p) => !p)}
        className={`group flex w-full items-center gap-3 rounded-xl px-3 py-2.5 transition-all duration-200 ${
          showPopup ? "bg-[#2ead81]/10" : "hover:bg-gray-50"
        }`}
      >
        {/* Avatar */}
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#2ead81] to-[#1a9a6e] shadow-sm">
          <LuUser className="h-4 w-4 text-white" />
        </div>
        {/* Name */}
        <div className="flex-1 text-left">
          <p className="text-sm font-bold leading-tight text-navy-700">{displayName}</p>
          <p className="text-[10px] leading-tight text-gray-400">{displayRole}</p>
        </div>
        <LuChevronUp
          className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${showPopup ? "rotate-180" : ""}`}
        />
      </button>

      {/* Popup menu */}
      {showPopup && (
        <div className="absolute bottom-14 left-0 right-0 z-[999] overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-xl">
          <div className="border-b border-gray-100 px-4 py-3">
            <p className="text-xs font-bold text-navy-700">{displayName}</p>
            <p className="text-[10px] text-gray-400">{displayRole}</p>
          </div>
          <div className="p-1.5 space-y-0.5">
            {isSuperAdmin && (
              <button
                onClick={handleAdminUserManagement}
                className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium text-gray-700 transition-all hover:bg-[#2ead81]/10 hover:text-[#2ead81]"
              >
                <LuUsers className="h-4 w-4" />
                Admin User Management
              </button>
            )}
            <button
              onClick={handleSignOut}
              className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium text-gray-700 transition-all hover:bg-red-50 hover:text-red-600"
            >
              <MdLogout className="h-4 w-4" />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const Sidebar = (props: {
  open: boolean;
  handleClick: any;
}) => {
  const { open, handleClick } = props;

  return (
    <div
      className={`sm:none duration-300 ease-in-out fixed !z-50 flex h-screen max-h-screen flex-col bg-white/90 backdrop-blur-xl pb-2 shadow-[4px_0_24px_rgba(0,0,0,0.02)] border-r border-gray-100 dark:border-white/5 transition-all dark:!bg-navy-900/90 dark:text-white ${open ? "w-[260px]" : "w-[70px]"
        }`}
    >
      {open ? (
        <div className="mx-6 mt-6 flex flex-col items-start">
          <img src={logo} alt="logo" style={{ width: "80px" }} />
          <span className="mt-1 ml-[65px] text-xs font-bold text-navy-700 dark:text-white">Admin</span>
        </div>
      ) : (
        <div className="h-14" />
      )}

      <button
        className={`absolute flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 transition-all hover:text-brand-500 dark:text-gray-400 dark:hover:text-brand-400 ${open ? "right-3 top-6" : "left-[19px] top-6"
          }`}
        onClick={handleClick}
      >
        <MdMenuOpen className={`h-5 w-5 transition-transform duration-300 ${!open && "rotate-180"}`} />
      </button>

      <div className="mx-4 mb-4 mt-2 h-px bg-gray-200 dark:bg-white/10" />

      {/* Nav item */}
      <ul className={`sidebar-main mb-auto flex flex-col gap-1 h-[calc(100vh-140px)] overflow-y-auto overflow-x-hidden ${open ? "px-3" : "px-1"}`}>
        <Links routes={routes} openSide={open} />
      </ul>
      {/* Nav item end */}

      {/* Profile Button (replaces Sign Out) */}
      <div className="px-3 pt-2.5 border-t border-gray-100 dark:border-white/10 mt-1">
        <ProfileButton open={open} />
      </div>
    </div>
  );
};

export default Sidebar;

